import { useCallback, useEffect, useState, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import { collection, getDocs, query, where, writeBatch } from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import {
  buildChecklistDrawingBoardQuery,
  loadChecklistCanvasProjectionDocs,
} from '../../services/checklistCanvasProjectionService'
import type { TimelineElement } from '../../context/TimelineContext'
import type { useRecurringLens } from '../../context/recurringLensContextCore'
import type { useWorkspace } from '../../context/useWorkspace'
import type { useAuth } from '../../context/useAuth'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import {
  CHECKLIST_CANVAS_LAYOUT_VERSION as WORKSPACE_CANVAS_LAYOUT_VERSION,
  CHECKLIST_CANVAS_NODES_COLLECTION,
  type ChecklistCanvasNode,
} from '../../types/checklistCanvas'
import { checklistCanvasNodeToChecklistItemProjection } from '../../utils/checklistCanvasProjection'
import { normalizeBoardItem } from './boardItem'
import {
  checklistBoardSessionSnapshotCache,
  cloneBoardSnapshot,
  type ChecklistSnapshotRow,
} from './sessionSnapshot'
import { overlayLayoutPositionsForWorkspace } from './liveReconciliation'

export function useBoardLoad({
  alwaysVisible,
  setTimerTick,
  item,
  recurringLens,
  setNodes,
  baselineNodesRef,
  undoStackRef,
  pendingDeletionsRef,
  setIsBoardDirty,
  setCanUndoBoard,
  setOptimisticPositions,
  isDashboardChecklistContainer,
  hasBoardSnapshotRef,
  activeWorkspace,
  currentUser,
  arrangeWorkspaceNodes,
  boardDocumentIdsRef,
  boardSessionSnapshotKey,
  isDebugCanvas,
  timelineChecklistItemsRef,
  isTimelineLoading,
  canEditCanvas,
  dashboardElements,
  deletedSourceIdsRef,
  boardRef,
  setViewportSize,
  setCanvasTransform,
}: {
  alwaysVisible: boolean
  setTimerTick: Dispatch<SetStateAction<number>>
  item: TimelineElement
  recurringLens: ReturnType<typeof useRecurringLens>
  setNodes: Dispatch<SetStateAction<BoardChecklistItem[]>>
  baselineNodesRef: MutableRefObject<BoardChecklistItem[]>
  undoStackRef: MutableRefObject<BoardChecklistItem[][]>
  pendingDeletionsRef: MutableRefObject<Set<string>>
  setIsBoardDirty: Dispatch<SetStateAction<boolean>>
  setCanUndoBoard: Dispatch<SetStateAction<boolean>>
  setOptimisticPositions: Dispatch<SetStateAction<Record<string, { x: number; y: number }>>>
  isDashboardChecklistContainer: (containerId: string) => boolean
  hasBoardSnapshotRef: MutableRefObject<boolean>
  activeWorkspace: ReturnType<typeof useWorkspace>['workspaces'][number] | undefined
  currentUser: ReturnType<typeof useAuth>['currentUser']
  arrangeWorkspaceNodes: (items: BoardChecklistItem[]) => BoardChecklistItem[]
  boardDocumentIdsRef: MutableRefObject<Set<string>>
  boardSessionSnapshotKey: string
  isDebugCanvas: boolean
  timelineChecklistItemsRef: MutableRefObject<BoardChecklistItem[]>
  isTimelineLoading: boolean
  canEditCanvas: boolean
  dashboardElements: TimelineElement[]
  deletedSourceIdsRef: MutableRefObject<Set<string>>
  boardRef: MutableRefObject<HTMLDivElement | null>
  setViewportSize: Dispatch<SetStateAction<{ width: number; height: number; scrollLeft: number; scrollTop: number }>>
  setCanvasTransform: (transform: { x: number; y: number; zoom: number }) => void
}) {
  // Only the fullscreen/maximized checklist canvas should lock out the app
  // header. The inline Board-section Checklists page reuses this component in
  // flat mode and should behave like the other rail pages.
  useEffect(() => {
    if (alwaysVisible) return
    document.body.classList.add('checklistCanvasOpen')
    return () => document.body.classList.remove('checklistCanvasOpen')
  }, [alwaysVisible])

  useEffect(() => {
    const intervalId = window.setInterval(() => setTimerTick((tick) => tick + 1), 60_000)
    return () => window.clearInterval(intervalId)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: setTimerTick are useRef/useState identities passed from the Surface (stable)
  }, [])

  // One-time load (no real-time): the board is loaded from Firestore once
  // per canvas identity and then treated as the local-first baseline until
  // the user saves, switches canvas identity, or reloads the page.
  const loadBoardFromDb = useCallback(async (
    opts: { preferSessionSnapshot?: boolean } = {},
  ) => {
    const { preferSessionSnapshot = true } = opts
    const checklistQuery = buildChecklistDrawingBoardQuery(firestoreDb, item.workspaceId)
    const checklistCanvasProjectionQuery = query(
      collection(firestoreDb, CHECKLIST_CANVAS_NODES_COLLECTION),
      where('workspaceId', '==', item.workspaceId),
    )

    // Overlay each node's position with the value from the current book's
    // layout version so recent drag/save results survive a reload. Runs
    // in parallel per (workspace, type) because the layout is per-book.
    const overlayLayoutPositions = (boardNodes: BoardChecklistItem[]) =>
      overlayLayoutPositionsForWorkspace(item.workspaceId, recurringLens, boardNodes)

    const adoptBoard = (boardNodes: BoardChecklistItem[]) => {
      void overlayLayoutPositions(boardNodes).then((overlaid) => {
        setNodes(overlaid)
        baselineNodesRef.current = overlaid
        undoStackRef.current = []
        pendingDeletionsRef.current.clear()
        setIsBoardDirty(false)
        setCanUndoBoard(false)
        setOptimisticPositions({})
      })
    }

    const adoptSessionSnapshot = (boardNodes: BoardChecklistItem[]) => {
      setNodes(boardNodes)
      baselineNodesRef.current = boardNodes
      undoStackRef.current = []
      pendingDeletionsRef.current.clear()
      setIsBoardDirty(false)
      setCanUndoBoard(false)
      setOptimisticPositions({})
    }

    const isDashboardSnapshotRow = (row: ChecklistSnapshotRow) => {
      const board = typeof row.data.board === 'string' ? row.data.board : 'DASHBOARD'
      return board === 'DASHBOARD' && isDashboardChecklistContainer(String(row.data.parentContainerId ?? ''))
    }

    const adoptChecklistRows = (rows: ChecklistSnapshotRow[]) => {
      hasBoardSnapshotRef.current = true

      const knownDocumentIds = new Set<string>()
      const seenRowIds = new Set<string>()
      const lensNodes: BoardChecklistItem[] = []
      const otherNodes: BoardChecklistItem[] = []

      rows.forEach((row, index) => {
        if (seenRowIds.has(row.id)) return
        seenRowIds.add(row.id)
        knownDocumentIds.add(row.id)
        const normalized = normalizeBoardItem(row.id, row.data, index)
        if (!normalized.createdById) {
          normalized.createdById = activeWorkspace?.ownerId ?? currentUser?.uid ?? ''
          normalized.createdByName = 'Workspace admin'
        }
        if (isDashboardChecklistContainer(normalized.parentContainerId) && normalized.board === 'DASHBOARD') {
          lensNodes.push(normalized)
        } else if (recurringLens) {
          otherNodes.push(normalized)
        }
      })

      lensNodes.sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)
      let mergedNodes = [...lensNodes]
      const needsWorkspaceLayout = mergedNodes.some((node) => node.workspaceCanvasLayoutVersion !== WORKSPACE_CANVAS_LAYOUT_VERSION)
      if (needsWorkspaceLayout) mergedNodes = arrangeWorkspaceNodes(mergedNodes)
      boardDocumentIdsRef.current = knownDocumentIds
      adoptBoard([...mergedNodes, ...otherNodes])
    }

    if (preferSessionSnapshot) {
      const cachedSnapshot = checklistBoardSessionSnapshotCache.get(boardSessionSnapshotKey)
      if (cachedSnapshot && cachedSnapshot.length > 0) {
        const snapshot = cloneBoardSnapshot(cachedSnapshot)
        hasBoardSnapshotRef.current = true
        boardDocumentIdsRef.current = new Set(snapshot.map((node) => node.id))
        adoptSessionSnapshot(snapshot)
        return
      }
    }

    if (isDebugCanvas) {
      hasBoardSnapshotRef.current = true
      boardDocumentIdsRef.current = new Set()
      adoptBoard([])
      return
    }

    try {
      if (recurringLens) {
        try {
          const { bookIdFor } = await import('../../types/checklistBooks')
          const { getPage, listDefinitionsForBook } = await import('../../services/checklistBookService')
          const { getLayoutVersion } = await import('../../services/layoutVersionService')
          const { getDetailVersion } = await import('../../services/checklistDetailVersionService')
          const { projectDefinitionToChecklistItem } = await import('../../services/checklistViewAdapter')
          const bookId = bookIdFor(item.workspaceId, recurringLens.mode)
          const page = await getPage(bookId, recurringLens.focusedKey).catch(() => null)
          const allTypes: Array<'INDIVIDUAL' | 'DAILY' | 'WEEKLY'> = ['INDIVIDUAL', 'DAILY', 'WEEKLY']
          const otherTypes = allTypes.filter((t) => t !== recurringLens.mode)
          const [definitions, otherDefinitionsLists, layoutVersion, detailVersion] = await Promise.all([
            listDefinitionsForBook(item.workspaceId, recurringLens.mode).catch(() => []),
            Promise.all(otherTypes.map((t) => listDefinitionsForBook(item.workspaceId, t).catch(() => []))),
            page?.layoutVersionId ? getLayoutVersion(bookId, page.layoutVersionId).catch(() => null) : Promise.resolve(null),
            page?.detailVersionId ? getDetailVersion(bookId, page.detailVersionId).catch(() => null) : Promise.resolve(null),
          ])
          const lensRows: ChecklistSnapshotRow[] = definitions.map((definition) => ({
            id: definition.id,
            data: projectDefinitionToChecklistItem(definition, {
              completion: page?.completions?.[definition.id],
              layoutNode: layoutVersion?.nodes?.[definition.id],
              detailVersion,
              pageDetailPatch: page?.detailPatch?.[definition.id] as Record<string, unknown> | undefined,
              pageKey: page?.pageKey ?? recurringLens.focusedKey,
            }),
          }))
          const lensIds = new Set(definitions.map((d) => d.id))
          const otherRows: ChecklistSnapshotRow[] = otherDefinitionsLists
            .flat()
            .filter((d) => !lensIds.has(d.id))
            .map((definition) => ({
              id: definition.id,
              data: projectDefinitionToChecklistItem(definition),
            }))
          adoptChecklistRows([...lensRows, ...otherRows])
          return
        } catch (recurringError) {
          console.warn('[ChecklistDrawingBoard] recurring book-model load failed; falling back to timeline rows', recurringError)
        }
      }

      const timelineRows = timelineChecklistItemsRef.current
        .filter((candidate) => candidate.workspaceId === item.workspaceId)
        .map((candidate) => ({ id: candidate.id, data: candidate }))
      const timelineDashboardCount = timelineRows.filter(isDashboardSnapshotRow).length

      if (!recurringLens && timelineDashboardCount > 0) {
        adoptChecklistRows(timelineRows)
        return
      }

      let projectionRows: ChecklistSnapshotRow[] = []
      try {
        const projectionDocs = await loadChecklistCanvasProjectionDocs(
          firestoreDb,
          item.workspaceId,
          timelineChecklistItemsRef.current
            .filter((c) => c.workspaceId === item.workspaceId)
            .map((c) => c.id),
          checklistCanvasProjectionQuery,
        )
        // Existing projection docs written before checklistType was persisted
        // do not carry it. Cross-reference the definitions we already loaded
        // from the timeline (which carry the correct type) so the individual
        // canvas filter — `type === 'INDIVIDUAL'` after `?? 'INDIVIDUAL'` —
        // doesn't sweep DAILY/WEEKLY items in.
        const timelineTypeById = new Map(
          timelineChecklistItemsRef.current
            .filter((c) => c.workspaceId === item.workspaceId)
            .map((c) => [c.id, c.checklistType])
        )
        projectionRows = projectionDocs.map((snapshotDocument) => {
          const canvasNode = {
            ...(snapshotDocument.data() as ChecklistCanvasNode),
            id: snapshotDocument.id,
          }
          if (!canvasNode.checklistType) {
            const inferred = timelineTypeById.get(snapshotDocument.id)
            if (inferred) canvasNode.checklistType = inferred
          }
          const projectedData = checklistCanvasNodeToChecklistItemProjection(canvasNode)
          return {
            id: snapshotDocument.id,
            data: {
              ...projectedData,
              sourceChecklistId: typeof projectedData.sourceChecklistId === 'string' ? projectedData.sourceChecklistId : null,
            },
          }
        })
      } catch {
        projectionRows = []
      }

      const projectionDashboardCount = projectionRows.filter(isDashboardSnapshotRow).length
      // Under a recurring lens (Daily / Weekly canvas) the projection is
      // insufficient: `checklist_canvas_nodes` docs carry position + display
      // fields but not `checklistType` or `occurrences`, so adopting from the
      // projection would produce items that fail the recurring display filter
      // (checklistType === lens.mode && occurrences[focusedKey] materialized)
      // and vanish on every reload. Fall through to the timeline path
      // (or the checklist_items query) to get the full item shape.
      if (projectionDashboardCount > 0 && !recurringLens && (timelineDashboardCount === 0 || projectionDashboardCount >= timelineDashboardCount)) {
        adoptChecklistRows(projectionRows)
        return
      }

      if (!isTimelineLoading || timelineRows.length > 0) {
        adoptChecklistRows(timelineRows)
        return
      }

      const snapshot = await getDocs(checklistQuery)
      if (canEditCanvas) {
        const legacyDependencyDocs = snapshot.docs.filter((snapshotDocument) => {
          const data = snapshotDocument.data() as Record<string, unknown>
          const hasLegacyLinks = ['blockedByNodeIds', 'isBlockerForNodeIds'].some((field) =>
            Array.isArray(data[field]) && data[field].some((link) =>
              link && typeof link === 'object' && (link as { targetType?: unknown }).targetType !== 'CHECKLIST'))
          return hasLegacyLinks || Boolean(data.blockedByExternal || data.blockedByExternalId || data.interWorkspaceTargetId)
        })
        for (let index = 0; index < legacyDependencyDocs.length; index += 400) {
          const cleanupBatch = writeBatch(firestoreDb)
          legacyDependencyDocs.slice(index, index + 400).forEach((snapshotDocument) => {
            const data = snapshotDocument.data() as Record<string, unknown>
            const checklistLinks = (field: string) => Array.isArray(data[field])
              ? data[field].filter((link) => link && typeof link === 'object' && (link as { targetType?: unknown }).targetType === 'CHECKLIST')
              : []
            cleanupBatch.update(snapshotDocument.ref, {
              blockedByNodeIds: checklistLinks('blockedByNodeIds'),
              isBlockerForNodeIds: checklistLinks('isBlockerForNodeIds'),
              blockedByExternal: null,
              blockedByExternalId: null,
              interWorkspaceTargetId: null,
            })
          })
          void cleanupBatch.commit().catch((error) => console.warn('[ChecklistDrawingBoard] legacy dependency cleanup failed', error))
        }
      }
      adoptChecklistRows(snapshot.docs.map((snapshotDocument) => ({
        id: snapshotDocument.id,
        data: snapshotDocument.data() as Partial<BoardChecklistItem> & { sourceChecklistId?: string | null; text?: string },
      })))
    } catch (error) {
      console.error('[ChecklistDrawingBoard] checklists load failed; rendering empty board', error)
      hasBoardSnapshotRef.current = false
      boardDocumentIdsRef.current = new Set()
      adoptBoard([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: baselineNodesRef, boardDocumentIdsRef, hasBoardSnapshotRef, pendingDeletionsRef, setCanUndoBoard, setIsBoardDirty, setNodes, setOptimisticPositions, timelineChecklistItemsRef, undoStackRef are useRef/useState identities passed from the Surface (stable)
  }, [activeWorkspace?.ownerId, arrangeWorkspaceNodes, boardSessionSnapshotKey, canEditCanvas, currentUser, dashboardElements, isDashboardChecklistContainer, isDebugCanvas, isTimelineLoading, item.workspaceId, recurringLens])

  // Bumped by the focused-page subscription below whenever the
  // `checklist_books/{bookId}/pages/{focusedKey}` doc changes remotely
  // (another user ticks the same checklist for the same occurrence, or a
  // server writer updates it). Included as an effect dep so
  // `loadBoardFromDb` re-runs and re-projects the visible nodes without
  // a page reload.
  const [focusedPageRefreshToken, setFocusedPageRefreshToken] = useState(0)

  useEffect(() => {
    deletedSourceIdsRef.current.clear()
    // Defer the view reset off the synchronous effect body (avoids cascading
    // renders); mirrors the original load behaviour.
    const resetTimer = window.setTimeout(() => {
      if (boardRef.current) {
        boardRef.current.scrollLeft = 0
        boardRef.current.scrollTop = 0
      }
      setViewportSize((current) => ({ ...current, scrollLeft: 0, scrollTop: 0 }))
      setCanvasTransform({ x: 0, y: 0, zoom: 1 })
    }, 0)
    void loadBoardFromDb()
    return () => window.clearTimeout(resetTimer)
    // Depend on primitive canvas identity only. Depending on `loadBoardFromDb`
    // itself caused this effect to re-fire every time the parent `item` prop
    // got a new reference — including after our own `persistChecklistMirror`
    // write — which clobbered locally-added gates with the stale fallback.
    // `item.id` still has to be included so switching from the workspace
    // default canvas to a goal/task/subtask canvas reloads Board-created
    // checklist files for that container.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.workspaceId, item.id, recurringLens?.focusedKey, recurringLens?.mode, focusedPageRefreshToken, setCanvasTransform])

  return {
    loadBoardFromDb,
    focusedPageRefreshToken,
    setFocusedPageRefreshToken,
  }
}
